import axios from "axios";
import { useAuthStore } from "../store/authStore";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "http://localhost:3001/api",
});

import { msalInstance } from "../services/authService";
import { loginRequest } from "../config/authConfig";

api.interceptors.request.use(async (config) => {
  const { token, authMethod, setToken, role, userEmail } = useAuthStore.getState();

  if (authMethod === "entra-id") {
    // Attempt to acquire token silently to ensure it's fresh
    try {
      const account = msalInstance.getAllAccounts()[0];
      if (account) {
        const response = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account: account
        });
        
        // Update store with new token if it changed
        if (response.idToken !== token) {
             setToken(response.idToken, role || "user", "entra-id", userEmail || undefined);
        }
        
        config.headers.Authorization = `Bearer ${response.idToken}`;
        return config;
      }
    } catch (error) {
       console.error("Silent token acquisition failed", error);
       // Fallback to stored token if silent refresh fails (might be expired, backend will reject)
       // Just let it proceed, the 401 response interceptor (if any) or user action will handle re-login
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 error and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const { authMethod, logout } = useAuthStore.getState();

      // If using Entra ID, try to acquire a new token
      if (authMethod === "entra-id") {
        try {
          const account = msalInstance.getAllAccounts()[0];
          if (account) {
            // Try interactive login if silent fails
            const response = await msalInstance.acquireTokenPopup({
              ...loginRequest,
              account: account
            });

            const { setToken, role, userEmail } = useAuthStore.getState();
            setToken(response.idToken, role || "user", "entra-id", userEmail || undefined);

            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${response.idToken}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          console.error("Token refresh failed", refreshError);
        }
      }

      // If refresh failed or not using Entra ID, logout and redirect
      logout();
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

export default api;
